import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../stores/authStore';
import { useBatteryStore } from '../stores/batteryStore';
import { useConfigStore } from '../stores/configStore';
import { getDropComments, addDropComment, deleteDrop, extendDrop, updateDrop, calculateRefund } from '../services/dropService';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase/app';
import { colors } from '../styles/colors';
import { IconClose } from './icons';
import type { LocationDrop, LocationDropComment } from '../types/location';

interface Props {
  dropId: string;
  onClose: () => void;
}

export default function DropDetailModal({ dropId, onClose }: Props) {
  const { i18n } = useTranslation();
  const isKo = i18n.language === 'ko';
  const user = useAuthStore((s) => s.user);
  const cells = useBatteryStore((s) => s.cells);
  const config = useConfigStore((s) => s.config);

  const [drop, setDrop] = useState<LocationDrop | null>(null);
  const [comments, setComments] = useState<LocationDropComment[]>([]);
  const [commentText, setCommentText] = useState('');
  const [loading, setLoading] = useState(true);
  const [remaining, setRemaining] = useState('');
  const [extendMin, setExtendMin] = useState(5);
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editContent, setEditContent] = useState('');

  const costPer5Min = config.dropCostPer5Min ?? 10000;
  const isMine = drop?.authorId === user?.uid;

  useEffect(() => {
    (async () => {
      const snap = await getDoc(doc(db, 'locationDrops', dropId));
      if (snap.exists()) {
        const d = { id: snap.id, ...snap.data() } as LocationDrop;
        setDrop(d);
        setEditTitle(d.title);
        setEditContent(d.content);
      }
      setComments(await getDropComments(dropId));
      setLoading(false);
    })();
  }, [dropId]);

  useEffect(() => {
    if (!drop) return;
    const update = () => {
      const ms = Math.max(0, drop.expiresAt.toMillis() - Date.now());
      const h = Math.floor(ms / 3600000);
      const m = Math.floor((ms % 3600000) / 60000);
      const s = Math.floor((ms % 60000) / 1000);
      setRemaining(h > 0 ? `${h}h ${m}m ${s}s` : `${m}m ${s}s`);
    };
    update();
    const iv = setInterval(update, 1000);
    return () => clearInterval(iv);
  }, [drop]);

  const handleAddComment = async () => {
    if (!commentText.trim() || !user) return;
    await addDropComment(dropId, { authorId: user.uid, authorNickname: user.nickname, content: commentText.trim() });
    setCommentText('');
    setComments(await getDropComments(dropId));
  };

  const handleDelete = async () => {
    const baseCost = config.dropBaseCost ?? 500;
    const refund = await calculateRefund(dropId, baseCost);
    const msg = isKo
      ? `삭제하시겠습니까?\n\n기본 비용 ${baseCost.toLocaleString()}셀은 환불되지 않습니다.\n환불 예정: ${refund.toLocaleString()}셀`
      : `Delete?\n\nBase cost ${baseCost.toLocaleString()} cells is non-refundable.\nRefund: ${refund.toLocaleString()} cells`;
    if (!confirm(msg)) return;
    const actualRefund = await deleteDrop(dropId, baseCost);
    if (actualRefund > 0 && user) await useBatteryStore.getState().charge(user.uid, actualRefund, 'DROP_CREATE');
    onClose();
  };

  const handleExtend = async () => {
    if (!user) return;
    const cost = Math.ceil(extendMin / 5) * costPer5Min;
    if (cells < cost) return;
    const msg = isKo
      ? `${extendMin < 60 ? `${extendMin}분` : `${extendMin / 60}시간`} 연장하시겠습니까?\n\n비용: ${cost.toLocaleString()}셀`
      : `Extend by ${extendMin < 60 ? `${extendMin}m` : `${extendMin / 60}h`}?\n\nCost: ${cost.toLocaleString()} cells`;
    if (!confirm(msg)) return;
    if (!(await useBatteryStore.getState().consume(user.uid, cost, 'DROP_CREATE'))) return;
    await extendDrop(dropId, extendMin, cost);
    const snap = await getDoc(doc(db, 'locationDrops', dropId));
    if (snap.exists()) setDrop({ id: snap.id, ...snap.data() } as LocationDrop);
  };

  const handleSaveEdit = async () => {
    if (!editTitle.trim()) return;
    await updateDrop(dropId, { title: editTitle.trim(), content: editContent.trim() });
    setDrop(d => d ? { ...d, title: editTitle.trim(), content: editContent.trim() } : d);
    setEditing(false);
  };

  const fmtTime = (ts: any) => {
    if (!ts) return '';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return `${d.getMonth() + 1}/${d.getDate()} ${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
  };

  if (loading || !drop) return null;

  const extendCost = Math.ceil(extendMin / 5) * costPer5Min;

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60" />
      <div className="relative z-10 w-full max-w-sm overflow-hidden rounded-2xl" style={{ backgroundColor: colors.surface, border: `2px solid #FFB300` }} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="px-4 py-3" style={{ borderBottom: `1px solid ${colors.border}`, backgroundColor: 'rgba(255,179,0,0.08)' }}>
          <div className="flex items-start justify-between">
            {editing ? (
              <div className="flex-1 mr-2">
                <input value={editTitle} onChange={(e) => setEditTitle(e.target.value)}
                  className="mb-1 w-full rounded border bg-transparent px-2 py-1.5 text-sm font-bold outline-none"
                  style={{ borderColor: '#FFB300', color: '#FFB300' }} />
                <div className="flex gap-1">
                  <button onClick={handleSaveEdit} className="rounded px-2 py-1 text-[10px] font-bold text-white" style={{ backgroundColor: colors.primary }}>{isKo ? '저장' : 'Save'}</button>
                  <button onClick={() => { setEditing(false); setEditTitle(drop.title); setEditContent(drop.content); }}
                    className="rounded px-2 py-1 text-[10px] font-bold" style={{ color: colors.textMuted }}>{isKo ? '취소' : 'Cancel'}</button>
                </div>
              </div>
            ) : (
              <div className="flex-1">
                <h3 className="text-sm font-bold" style={{ color: '#FFB300' }}>📍 {drop.title}</h3>
                <div className="flex items-center gap-2 mt-0.5">
                  <button onClick={() => window.dispatchEvent(new CustomEvent('open-profile', { detail: { uid: drop.authorId, nickname: drop.authorNickname } }))}
                    className="text-[10px] font-semibold hover:underline" style={{ color: colors.textSecondary }}>{drop.authorNickname}</button>
                  <span className="font-lcd text-[10px] font-bold" style={{ color: '#FFB300' }}>{remaining}</span>
                </div>
              </div>
            )}
            <div className="flex shrink-0 items-center gap-1">
              {isMine && !editing && <button onClick={() => setEditing(true)} className="rounded px-1.5 py-0.5 text-[10px] font-bold" style={{ color: colors.primary, border: `1px solid ${colors.primary}40` }}>{isKo ? '편집' : 'Edit'}</button>}
              <button onClick={onClose}><IconClose size={16} color={colors.textMuted} /></button>
            </div>
          </div>
        </div>

        <div className="max-h-80 overflow-y-auto">
          {/* Content */}
          <div className="px-4 py-3" style={{ borderBottom: `1px solid ${colors.border}` }}>
            {editing ? (
              <textarea value={editContent} onChange={(e) => setEditContent(e.target.value)} rows={3}
                className="w-full rounded border bg-transparent px-2 py-1 text-sm outline-none"
                style={{ borderColor: colors.border, color: colors.text }} />
            ) : (
              <p className="text-sm whitespace-pre-wrap" style={{ color: drop.content ? colors.text : colors.textMuted }}>
                {drop.content || (isKo ? '(내용 없음)' : '(No content)')}
              </p>
            )}
          </div>

          {/* Owner actions */}
          {isMine && !editing && (
            <div className="flex items-center gap-2 px-4 py-2" style={{ borderBottom: `1px solid ${colors.border}` }}>
              <select value={extendMin} onChange={(e) => setExtendMin(Number(e.target.value))}
                className="rounded px-2 py-1.5 text-xs font-bold"
                style={{ backgroundColor: colors.card, color: colors.text, border: `1px solid ${colors.border}` }}>
                {[5, 10, 30, 60, 120].map(m => <option key={m} value={m}>{m < 60 ? `${m}${isKo ? '분' : 'm'}` : `${m/60}${isKo ? '시간' : 'h'}`}</option>)}
              </select>
              <button onClick={handleExtend} disabled={cells < extendCost}
                className="rounded px-2.5 py-1.5 text-[10px] font-bold text-white disabled:opacity-50" style={{ backgroundColor: colors.primary }}>
                +{isKo ? '연장' : 'Extend'} ({extendCost.toLocaleString()})
              </button>
              <div className="flex-1" />
              <button onClick={handleDelete}
                className="rounded px-2.5 py-1.5 text-[10px] font-bold" style={{ color: colors.error, border: `1px solid ${colors.error}40` }}>
                {isKo ? '삭제 (환불)' : 'Delete'}
              </button>
            </div>
          )}

          {/* Comments */}
          <div className="px-4 py-2">
            <p className="mb-2 text-xs font-bold" style={{ color: colors.textMuted }}>{isKo ? '댓글' : 'Comments'} ({comments.length})</p>
            {comments.map((c) => (
              <div key={c.id} className="mb-1.5 rounded-lg px-3 py-2" style={{ backgroundColor: colors.card }}>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold" style={{ color: colors.textSecondary }}>{c.authorNickname}</span>
                  <span className="text-[10px]" style={{ color: colors.textMuted }}>{fmtTime(c.createdAt)}</span>
                </div>
                <p className="text-sm" style={{ color: colors.text }}>{c.content}</p>
              </div>
            ))}
            <div className="mt-2 flex gap-1">
              <input type="text" value={commentText} onChange={(e) => setCommentText(e.target.value)}
                placeholder={isKo ? '댓글...' : 'Comment...'}
                className="flex-1 rounded-lg border bg-transparent px-2 py-1.5 text-xs outline-none"
                style={{ borderColor: colors.border, color: colors.text }}
                onKeyDown={(e) => { if (e.key === 'Enter') handleAddComment(); }} />
              <button onClick={handleAddComment} disabled={!commentText.trim()}
                className="rounded-lg px-3 py-1.5 text-xs font-bold text-white disabled:opacity-50"
                style={{ backgroundColor: colors.primary }}>
                {isKo ? '등록' : 'Post'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
